<template>
  <div class="full">
    <div class="toolbar" :style="{ left: toolbar.x + 'px', top: toolbar.y + 'px' }">
      <div class="toolbar-grab" draggable="true" @dragstart.prevent="handleToolbarGrab"></div>
      <div class="tool-list">
        <div class="tool" v-for="tool of toolbar.tools" :key="tool.id" @click.prevent="activeTool = tool.id"
          :data-active="tool.id == activeTool">
          <img class="icon" :src="tool.icon" :alt="tool.label">
          <span class="name">{{ tool.label }}</span>
          <span class="key">{{ tool.key }}</span>
        </div>
      </div>
    </div>
    <div class="form-pages">
      <div class="form-page" v-for="(pageImage, idx) in pageImages" :key="idx" :style="getPageStyle(pageImage)"
        @mousedown.prevent="handlePageClick($event, idx)">
        <div class="page-annot" v-for="pageAnnot of getPageAnnots(idx)" :key="pageAnnot.key" :data-key="pageAnnot.key"
          :style="getAnnotStyle(pageAnnot)" @mousedown.prevent.stop="handleClickAnnot($event, pageAnnot)"
          :data-selected="isAnnotSelected(pageAnnot)">
          <span v-if="pageAnnot.type == 'DATE'">YYYY-MM-DD</span>
          <span v-if="pageAnnot.type == 'PHONE'">(123) 123-1234</span>
          <div class="resize-handle" v-if="pageAnnot.type != 'CHECKBOX' && isAnnotSelected(pageAnnot)"
            @mousedown.prevent.stop="handleResizeAnnot($event, pageAnnot)"></div>
        </div>
      </div>
      <button @click="attemptSaveChanges">Save Changes</button>
    </div>
    <div class="tool-shadow" v-if="activeTool && shadow.visible" :style="getShadowStyle()">

    </div>
  </div>
</template>

<script>
import * as api from '@/api'
import TextboxIcon from '@/assets/pdf-editor-icons/textbox.png'
import DateboxIcon from '@/assets/pdf-editor-icons/datebox.png'
import CheckboxIcon from '@/assets/pdf-editor-icons/checkbox.png'
import SignatureIcon from '@/assets/pdf-editor-icons/signature.png'
import SelectIcon from '@/assets/pdf-editor-icons/select.png'

export default {
  name: 'PDFFormEditorView',
  data() {
    return {
      formInfo: null,
      pageImages: [],
      selectedAnnots: new Set(),
      activeTool: 'SELECT',
      toolbar: {
        tools: [
          { id: 'SELECT', label: 'Select', icon: SelectIcon, key: 'Q' },
          { id: 'TEXT', label: 'Textbox', icon: TextboxIcon, key: 'T' },
          { id: 'DATE', label: 'Datebox', icon: DateboxIcon, key: 'D' },
          { id: 'PHONE', label: 'Phonebox', icon: DateboxIcon, key: 'R' },
          { id: 'CHECKBOX', label: 'Checkbox', icon: CheckboxIcon, key: 'C' },
          { id: 'SIGNATURE', label: 'Signature', icon: SignatureIcon, key: 'S' },
        ],
        x: 10,
        y: 10,
        grabbing: false,
        grabOffX: 0,
        grabOffY: 0,
      },
      shadow: {
        visible: false,
        x: 0,
        y: 0,
        lastMouseX: 0,
        lastMouseY: 0,
        width: 0,
        height: 0,
        lockX: 0,
        lockY: 0
      },
      isShiftDown: false,
      isSelecting: false,
      selectionX: 0,
      selectionY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      isResizing: false,
      resizingAnnot: null,
      resizingPageEl: null
    }
  },
  mounted() {
    this.fetchFormInfo();
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  },
  unmounted() {
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  },
  methods: {
    async fetchFormInfo() {
      const formId = this.$route.params.id;
      try {
        this.formInfo = await api.latestVersion(formId);

        if (!this.formInfo.filePages || this.formInfo.filePages.length == 0) {
          this.formInfo.filePages = this.formInfo.filePagePaths.map((_, idx) => ({
            page: idx + 1,
            pageAnnotations: []
          }))
        }
        console.log(this.formInfo);

        this.loadPageImages();
      } catch (err) {
        alert('Failed to fetch form info');
      }
    },
    async loadPageImages() {
      this.pageImages = await Promise.all(this.formInfo.filePagePaths
        .map(path => new Promise((resolve, reject) => {
          const image = new Image();
          image.src = process.env.VUE_APP_PUBLIC_IMAGES + path;
          image.crossOrigin = 'Anonymous'
          image.onload = () => resolve(image);
          image.onerror = (err) => reject(err);
        }))
      );
    },
    async attemptSaveChanges() {
      try {

        const ROW_THRESH = 0.01;
        this.formInfo.filePages = this.formInfo.filePages.map(page => {
          page.pageAnnotations = page.pageAnnotations
            .sort((a, b) => {
              // Sort correctly so tab is nice
              if (Math.abs(a.y - b.y) < ROW_THRESH) {
                return a.x - b.x;
              }
              return b.y - a.y;
            })
            .filter(annot => {
              if (annot.width < 0.001) {
                return false;
              }
              if (annot.height < 0.001) {
                return false;
              }

              return true;
            })
            .map((annot) => {
              // Bound to page
              if (annot.width > 1) annot.width = 1;
              if (annot.height > 1) annot.height = 1;
              if (annot.x < 0) annot.x = 0;
              if (annot.y < 0) annot.y = 0;
              if (annot.x + annot.width > 1) annot.x = 1 - annot.width;
              if (annot.y + annot.height > 1) annot.y = 1 - annot.height;
              return annot;
            })

          return page;
        })
        await api.updateFormAnnotations(this.formInfo.id, this.formInfo.filePages);
        this.$router.push(`/forms/${this.formInfo.id}`)
      } catch (err) {
        console.error(err);
      }
    },
    getPageStyle(pageImage) {
      return {
        aspectRatio: pageImage.width / pageImage.height,
        backgroundImage: `url(${pageImage.src})`
      }
    },
    getPageAnnots(pageIndex) {
      const filePages = this.formInfo.filePages;
      if (!filePages) return [];

      const filePage = filePages[pageIndex];
      if (!filePage) return [];

      return filePage.pageAnnotations;
    },
    getAnnotStyle(annot) {
      return {
        left: (annot.x * 100) + '%',
        bottom: (annot.y * 100) + '%',
        width: (annot.width * 100) + '%',
        height: (annot.height * 100) + '%'
      }
    },
    recomputeShadow() {
      const sh = this.shadow;
      let cx = sh.lastMouseX;
      let cy = sh.lastMouseY;

      if (this.isShiftDown) {
        const dx = Math.abs(cx - sh.lockX);
        const dy = Math.abs(cy - sh.lockY);
        if (dx > dy) {
          cy = sh.lockY;
        } else {
          cx = sh.lockX;
        }
      }

      if (this.activeTool && this.activeTool != 'SELECT') {
        switch (this.activeTool) {
          case 'CHECKBOX':
            sh.x = cx - 12;
            sh.y = cy - 12;
            sh.width = 24;
            sh.height = 24;
            break;
          case 'SIGNATURE':
            sh.x = cx - 5;
            sh.y = cy - 20;
            sh.width = 60;
            sh.height = 40;
            break;
          default:
            sh.x = cx - 5;
            sh.y = cy - 10;
            sh.width = 30;
            sh.height = 20;
            break;
        }

        sh.visible = true;
      } else if (this.activeTool == 'SELECT' && this.isSelecting) {

        if (cx > this.selectionX) {
          sh.x = this.selectionX;
          sh.width = cx - this.selectionX;
        } else {
          sh.x = cx;
          sh.width = this.selectionX - cx;
        }

        if (cy > this.selectionY) {
          sh.y = this.selectionY;
          sh.height = cy - this.selectionY;
        } else {
          sh.y = cy;
          sh.height = this.selectionY - cy;
        }


        sh.visible = true;
      } else {
        sh.visible = false;
      }
    },
    getShadowStyle() {
      const style = {
        left: this.shadow.x + 'px',
        top: this.shadow.y + 'px',
        width: this.shadow.width + 'px',
        height: this.shadow.height + 'px'
      }

      return style;
    },
    handleToolbarGrab(event) {
      const tb = this.toolbar;
      tb.grabOffX = event.pageX - tb.x;
      tb.grabOffY = event.pageY - tb.y;
      tb.grabbing = true;
      console.log(tb);
    },
    handleMouseMove(event) {
      const tb = this.toolbar;
      if (tb.grabbing) {
        tb.x = event.pageX - tb.grabOffX;
        tb.y = event.pageY - tb.grabOffY;
      }

      // Recompute shadow
      const sh = this.shadow;
      sh.lastMouseX = event.clientX;
      sh.lastMouseY = event.clientY;
      this.recomputeShadow();

      // Dragging
      if (this.isDragging) {
        const dx = event.clientX - this.dragStartX;
        const dy = event.clientY - this.dragStartY;
        const annots = this.$el.querySelectorAll('.page-annot');
        for (let annot of annots) {
          const key = annot.getAttribute('data-key');
          if (!this.selectedAnnots.has(key)) continue;
          annot.style.transform = `translate(${dx}px, ${dy}px)`
        }
      }

      // Resizing
      if (this.isResizing) {
        this.resizingAnnotEl.style.transition = 'none';
        const bb = this.resizingPageEl.getBoundingClientRect();
        const a = this.resizingAnnot;
        const x = a.x * bb.width + bb.left;
        const xc = event.clientX;
        a.width = (xc - x) / bb.width;
      }
    },
    handleMouseUp() {
      const tb = this.toolbar;
      if (tb.grabbing) {
        tb.grabbing = false;
      }

      const sh = this.shadow;
      if (this.activeTool == 'SELECT' && this.isSelecting) {
        this.isSelecting = false;
        sh.visible = false;

        const annots = this.$el.querySelectorAll('.page-annot');
        this.selectedAnnots.clear();
        for (let annot of annots) {
          const bb = annot.getBoundingClientRect();
          if (bb.left < sh.x || bb.right > sh.x + sh.width) continue;
          if (bb.top < sh.y || bb.bottom > sh.y + sh.height) continue;
          this.selectedAnnots.add(annot.getAttribute('data-key'));
        }

        sh.width = 0;
        sh.height = 0;
      }

      if (this.isDragging) {
        this.isDragging = false;

        const annots = this.$el.querySelectorAll('.page-annot');
        for (let annotEl of annots) {
          const key = annotEl.getAttribute('data-key');
          if (!this.selectedAnnots.has(key)) continue;

          const annot = this.formInfo.filePages
            .flatMap(fp => fp.pageAnnotations)
            .find(a => a.key == key);

          if (annot) {
            const parentBB = annotEl.parentElement.getBoundingClientRect();
            const bb = annotEl.getBoundingClientRect();
            annot.x = (bb.x - parentBB.x) / parentBB.width;
            annot.y = 1 - (bb.y - parentBB.y + bb.height) / parentBB.height;
          }

          annotEl.style.transform = '';
        }
      }

      if (this.isResizing) {
        this.isResizing = false;
        this.resizingAnnotEl.style.transition = '';
      }
    },
    handlePageClick(event, pageIndex) {

      if (this.activeTool && this.activeTool != 'SELECT') {
        const el = event.currentTarget;
        const bb = el.getBoundingClientRect();

        let sh = this.shadow;
        let x = (sh.x - bb.left) / bb.width;
        let y = (bb.top + bb.height - sh.y - sh.height) / bb.height;
        let w = sh.width / bb.width;
        let h = sh.height / bb.height;
        const newAnnot = {
          key: this.generateRandomId('doNotImport'),
          type: this.activeTool,
          x: x,
          y: y,
          width: w,
          height: h,
          optional: true
        }

        const filePages = this.formInfo.filePages;
        const filePage = filePages[pageIndex];
        filePage.pageAnnotations.push(newAnnot)

        this.$nextTick(() => {
          if (['TEXT', 'DATE', 'PHONE', 'SIGNATURE'].includes(this.activeTool)) {
            const img = this.pageImages[pageIndex];
            const cnv = document.createElement('canvas');
            const ctx = cnv.getContext('2d');

            const clickX = Math.floor((sh.lastMouseX - bb.left) * (img.width / bb.width));
            const ix = 0;
            const iw = img.width;
            const ih = Math.floor(newAnnot.height * img.height) + 10; // Put 10 pixels on the bottom for good measure
            const iy = Math.floor((sh.y - bb.top) * (img.height / bb.height));

            cnv.width = iw;
            cnv.height = ih;
            ctx.drawImage(img, ix, iy, iw, ih, 0, 0, iw, ih);
            const imageData = ctx.getImageData(0, 0, iw, ih);
            const data = imageData.data;

            let startCol = clickX;
            let endCol = clickX;

            // Calculate the position of the line (using first dark spot)
            let lineRow = 0;
            for (let row = ih; row >= 0; row--) {
              const index = (row * iw + clickX) * 4;
              const r = data[index];
              const g = data[index + 1];
              const b = data[index + 2];
              const brightness = (r + g + b) / 3;

              if (brightness < 150) {
                lineRow = row;
                break;
              }
            }

            // Find the start point
            for (let col = clickX; col >= 0; col--) {
              const index = (lineRow * iw + col) * 4;
              const r = data[index];
              const g = data[index + 1];
              const b = data[index + 2];
              const brightness = (r + g + b) / 3;

              // average line is brighter than 100? stop here
              if (brightness > 220) {
                startCol = col;
                break;
              }
            }

            // Find the end point
            for (let col = clickX; col < iw; col++) {
              const index = (lineRow * iw + col) * 4;
              const r = data[index];
              const g = data[index + 1];
              const b = data[index + 2];
              const brightness = (r + g + b) / 3;

              // average line is brighter than 100? stop here
              if (brightness > 220) {
                endCol = col;
                break;
              }
            }

            // Update the annotation
            let newW = (endCol - startCol - 20) / img.width;
            if (newW > newAnnot.width) {
              newAnnot.y -= lineRow / img.height - newAnnot.height;
              newAnnot.x = (startCol + 10) / img.width;
              newAnnot.width = (newW);
            }
          }
        })
      }

      if (this.activeTool == 'SELECT' && !this.isSelecting) {
        this.selectedAnnots.clear();
        this.isSelecting = true;
        this.selectionX = event.clientX;
        this.selectionY = event.clientY;
      }

    },
    handleClickAnnot(event, annot) {
      const annotKey = annot.key;

      if (this.isShiftDown) {
        if (this.selectedAnnots.has(annotKey)) {
          this.selectedAnnots.delete(annotKey);
        } else {
          this.selectedAnnots.add(annotKey);
        }
      } else if (!this.selectedAnnots.has(annotKey)) {
        this.selectedAnnots.clear();
        this.selectedAnnots.add(annotKey);
        if (annot.group) {
          this.formInfo.filePages
            .flatMap(fp => fp.pageAnnotations)
            .filter(a => a.key != annotKey && a.group && a.group.id == annot.group.id)
            .forEach(a => this.selectedAnnots.add(a.key));
        }
      } else {
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.isDragging = true;
      }
    },
    handleResizeAnnot(event, annot) {
      const handleEl = event.currentTarget;
      const annotEl = handleEl.parentElement;
      const pageEl = annotEl.parentElement;

      this.isResizing = true;
      this.resizingAnnot = annot;
      this.resizingAnnotEl = annotEl;
      this.resizingPageEl = pageEl;
    },
    handleKeyDown(event) {
      const key = event.key.toUpperCase();
      const tool = this.toolbar.tools.find(t => t.key == key);
      if (tool) {
        this.activeTool = tool.id;
        this.recomputeShadow();
      }

      if (key == 'SHIFT' && !this.isShiftDown) {
        const sh = this.shadow;
        sh.lockX = sh.lastMouseX;
        sh.lockY = sh.lastMouseY;
        this.isShiftDown = true;
      }

      if (key == 'BACKSPACE' || key == 'DELETE') {
        for (let filePage of this.formInfo.filePages) {
          console.log(this.selectedAnnots);
          filePage.pageAnnotations = filePage.pageAnnotations.filter(annot => {
            return !this.selectedAnnots.has(annot.key)
          });
        }
        this.selectedAnnots.clear();
      }

      if (key == 'G' && event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        const annots = this.formInfo.filePages
          .flatMap(fp => fp.pageAnnotations)
          .filter(a => this.selectedAnnots.has(a.key))

        let randomId = 'GRP';
        for (let i = 0; i < 10; i++) {
          randomId += Math.floor(Math.random() * 10);
        }
        for (let annot of annots) {
          if (annot.type == 'CHECKBOX') {
            annot.group = {
              id: randomId,
              rule: 'FILL_ONE'
            }
          }
        }
      }
    },
    handleKeyUp(event) {
      const key = event.key.toUpperCase();
      if (key == 'SHIFT') {
        this.isShiftDown = false;
      }
    },
    generateRandomId(prefix, length = 12) {
      let randomId = prefix;

      for (let i = 0; i < length; i++) {
        randomId += Math.floor(Math.random() * 10);
      }

      return randomId;
    },
    isAnnotSelected(annot) {
      return this.selectedAnnots.has(annot.key);
    }
  }
}
</script>

<style scoped>
.form-page {
  width: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  position: relative;
}

.form-pages {
  padding: 20px;
  width: 100%;
  max-width: 1000px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-annot {
  position: absolute;
  background: #2d56f922;
  cursor: pointer;
  transition: width ease-in-out 0.1s;
  border-radius: 2px;
  overflow: hidden;
}

.page-annot span {
  white-space: nowrap;
}

.page-annot:hover {
  box-shadow: 0px 0px 4px #00000066;
}

.page-annot[data-selected="true"] {
  outline: 2px dashed green;
  box-shadow: inset 0px 0px 0px 50px #00FF0055;
  cursor: grab;
}

.resize-handle {
  z-index: 10;
  position: absolute;
  right: 0px;
  width: 5px;
  height: 100%;
  background: #00800055;
  cursor: ew-resize;
}

.toolbar {
  z-index: 100;
  position: fixed;
  width: 200px;
  background: #FFF;
  box-shadow: 0px 0px 12px #00000022;
}

.toolbar-grab {
  cursor: grab;
  width: 100%;
  height: 30px;
  background: #EEE;
  border-bottom: 1px solid #CCC;
}

.tool-list {
  padding: 5px;
}

.tool {
  padding: 10px;
  display: flex;
  gap: 10px;
  border-radius: 5px;
}

.tool:hover {
  cursor: pointer;
  background: #F5F5F5;
}

.tool[data-active="true"] {
  background: #afdcff;
}

.tool .icon {
  width: 20px;
  height: 20px;
  display: block;
}

.tool .name {
  flex: 1;
}

.tool .key {
  width: 20px;
  height: 20px;
  border: 1px solid #00000022;
  text-align: center;
  background: #FFF;
}

.tool-shadow {
  position: fixed;
  background: #2d56f9;
  opacity: 0.2;
  width: 200px;
  height: 20px;
  pointer-events: none;
}

canvas {
  width: 100%;
}
</style>